import {
  getRequiredFields,
  removeEmptyFields,
  setEmptyFieldsToNull,
} from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import {
  CURRICULUM_NOTES,
  DESIGNATION,
  EMAIL,
  INCLUDES_3D_PRINTER,
  INCLUDES_CHAIRS,
  INCLUDES_ELECTRICAL,
  INCLUDES_PAINTING,
  INCLUDES_PODIUM_MIC,
  INCLUDES_STORAGE_CABINETS,
  INCLUDES_TABLETS,
  INCLUDES_TEACHERS_TABLE,
  INCLUDES_TV,
  INFRASTRUCTURE_NOTES,
  NAME,
  OTHER_NOTES,
  PHONE,
  POC_DETAILS,
} from "./consts";
import { formValues, schema } from "./validations";
import { useNavigate, useParams } from "react-router-dom";
import { getSchoolRedirectPath } from "@/routes/utils";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { clearSchoolOnboardingId } from "@/redux/slices/currentDataSlice";
import { useEffect, useRef } from "react";
import {
  useCreateSchoolInventoryDetailMutation,
  useCreateSchoolLabPocMutation,
  useGetSchoolInventoryDetailBySchoolIdQuery,
  useGetSchoolLabPocBySchoolIdQuery,
} from "@/services";

const useLabInventoryInfo = () => {
  const navigate = useNavigate();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const { id, action } = useParams();
  const dispatch = useAppDispatch();
  
  const loggedInUserData = useSelector(
    (state: RootState) => state.currentData.loggedInUserData
  );
  // Get school ID from Redux store
  const schoolOnboardingId = useAppSelector(
    (state: RootState) => state.currentData.schoolOnboardingId
  );
  const schoolId = String(id || schoolOnboardingId || "");
  const isEdit = action === "edit" && !!schoolId;

  const form = useForm<formValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      [POC_DETAILS]: Array.from({ length: 1 }, () => ({
        [NAME]: "",
        [PHONE]: "",
        [EMAIL]: "",
        [DESIGNATION]: "",
      })),
      [INCLUDES_TV]: false,
      [INCLUDES_3D_PRINTER]: false,
      [INCLUDES_PAINTING]: false,
      [INCLUDES_ELECTRICAL]: false,
      [INCLUDES_STORAGE_CABINETS]: false,
      [INCLUDES_TEACHERS_TABLE]: false,
      [INCLUDES_PODIUM_MIC]: false,
      [INCLUDES_TABLETS]: false,
      [INCLUDES_CHAIRS]: false,
      [INFRASTRUCTURE_NOTES]: "",
      [CURRICULUM_NOTES]: "",
      [OTHER_NOTES]: "",
    },
  });

  const requiredFields = getRequiredFields(schema);

  const { mutateAsync: createLabPoc } = useCreateSchoolLabPocMutation();
  const { mutateAsync: createInventoryDetail } =
    useCreateSchoolInventoryDetailMutation();

  // Edit mode: fetch and prefill
  const { data: labPocResp } = useGetSchoolLabPocBySchoolIdQuery(schoolId, {
    enabled: isEdit,
  } as any);
  const { data: inventoryResp } = useGetSchoolInventoryDetailBySchoolIdQuery(
    schoolId,
    { enabled: isEdit } as any
  );

  useEffect(() => {
    if (!isEdit) return;

    const pocList = ((labPocResp as any)?.data as any[]) || [];
    const inv = (inventoryResp as any)?.data || {};

    const mappedPoc = pocList.length
      ? pocList.map((it) => ({
          [NAME]: String(it?.pocName ?? ""),
          [PHONE]: String(it?.pocPhone ?? ""),
          [EMAIL]: String(it?.pocEmail ?? ""),
          [DESIGNATION]: String(it?.pocDesignation ?? ""),
        }))
      : undefined;

    form.reset({
      ...(form.getValues() as any),
      ...(mappedPoc ? { [POC_DETAILS]: mappedPoc as any } : {}),
      [INCLUDES_TV]: Boolean(inv?.includesTv ?? false),
      [INCLUDES_3D_PRINTER]: Boolean(inv?.is3DPrinterIncluded ?? false),
      [INCLUDES_PAINTING]: Boolean(inv?.isPaintingIncluded ?? false),
      [INCLUDES_ELECTRICAL]: Boolean(inv?.isElectricalIncluded ?? false),
      [INCLUDES_STORAGE_CABINETS]: Boolean(
        inv?.isStorageCabinetsIncluded ?? false
      ),
      [INCLUDES_TEACHERS_TABLE]: Boolean(inv?.isTeachersTableIncluded ?? false),
      [INCLUDES_PODIUM_MIC]: Boolean(inv?.isPodiumIncluded ?? false),
      [INCLUDES_TABLETS]: Boolean(inv?.isTabletsIncluded ?? false),
      [INCLUDES_CHAIRS]: Boolean(inv?.isChairsIncluded ?? false),
      [INFRASTRUCTURE_NOTES]: String(inv?.infrastructureNotes ?? ""),
      [CURRICULUM_NOTES]: String(inv?.curriculumNotes ?? ""),
      [OTHER_NOTES]: String(inv?.otherNotes ?? ""),
    } as any);
  }, [isEdit, labPocResp, inventoryResp]);

  const onSubmit = async (data: formValues) => {
    const cleaned = setEmptyFieldsToNull(removeEmptyFields(data)) as any;

    // 1) Save Lab POC list (filter out empty rows)
    const pocList = (cleaned[POC_DETAILS] as any[] | undefined)?.filter(
      (it) => it && (it[NAME] || it[PHONE] || it[EMAIL] || it[DESIGNATION])
    );

    if (schoolId && pocList && pocList.length > 0) {
      await createLabPoc({
        schoolId,
        pocList: pocList.map((it) => ({
          pocName: String(it[NAME] || ""),
          pocPhone: String(it[PHONE] || ""),
          pocEmail: String(it[EMAIL] || ""),
          pocDesignation: String(it[DESIGNATION] || ""),
        })),
      });
    }

    // 2) Save Inventory details
    if (schoolId) {
      await createInventoryDetail({
        schoolId,
        isTvIncluded: Boolean(cleaned[INCLUDES_TV]),
        is3DPrinterIncluded: Boolean(cleaned[INCLUDES_3D_PRINTER]),
        isPaintingIncluded: Boolean(cleaned[INCLUDES_PAINTING]),
        isElectricalIncluded: Boolean(cleaned[INCLUDES_ELECTRICAL]),
        isStorageCabinetsIncluded: Boolean(cleaned[INCLUDES_STORAGE_CABINETS]),
        isTeachersTableIncluded: Boolean(cleaned[INCLUDES_TEACHERS_TABLE]),
        isPodiumIncluded: Boolean(cleaned[INCLUDES_PODIUM_MIC]),
        isTabletsIncluded: Boolean(cleaned[INCLUDES_TABLETS]),
        isChairsIncluded: Boolean(cleaned[INCLUDES_CHAIRS]),
        infrastructureNotes: cleaned[INFRASTRUCTURE_NOTES] || "",
        curriculumNotes: cleaned[CURRICULUM_NOTES] || "",
        otherNotes: cleaned[OTHER_NOTES] || "",
      });
    }

    // Clear school onboarding ID from Redux store when all three stages are completed
    dispatch(clearSchoolOnboardingId());
    
    const userRole = loggedInUserData?.role || [];
    navigate(getSchoolRedirectPath(userRole));
  };

  const onCancel = () => {
    const userRole = loggedInUserData?.role || [];
    navigate(getSchoolRedirectPath(userRole));
  };

  const handleScrollToBottom = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: "smooth", // smooth scroll
      });
    }
  };

  const onAddNewPocDetails = () => {
    const existing = form.getValues(POC_DETAILS) || [];
    form.setValue(POC_DETAILS, [
      ...existing,
      {
        [NAME]: "",
        [EMAIL]: "",
        [PHONE]: "",
        [DESIGNATION]: "",
      },
    ]);
    setTimeout(() => handleScrollToBottom(), 200);
  };

  const onRemovePocDetails = (index: number) => {
    const details = [...(form.getValues(POC_DETAILS) || [])];
    details.splice(index, 1);
    form.setValue(POC_DETAILS, details);
    setTimeout(() => handleScrollToBottom(), 200);
  };

  return {
    form,
    requiredFields,
    onSubmit,
    onCancel,
    onAddNewPocDetails,
    onRemovePocDetails,
    scrollRef,
  };
};

export default useLabInventoryInfo;
